import { Component, OnInit, OnDestroy } from "@angular/core";
import { first } from "rxjs/operators";
import { Subscription } from "rxjs";
import { MatLegacyDialog as MatDialog, MatLegacyDialogConfig as MatDialogConfig} from "@angular/material/legacy-dialog";
import * as _ from "lodash";
import { Router } from "@angular/router";

import { AppGlobals } from "../app.globals";
import { StorageService, AuthenticationService, MediaWatcherService, LookupsService, OrientationService, PermitToStayService } from "../_services";
import { IUser } from "../_models";
import { OrientationPreArrivalFormDialogComponent } from "./preArrivalForm";
import { OrientationCheckListItemsDialogComponent } from "./checkListItems";

@Component({
    templateUrl: "orientation.component.html",
    styleUrls: ["orientation.component.css"]
})
export class OrientationComponent implements OnInit, OnDestroy {
    studentOrientationSemesters = null;
    isMobile = false;
    today = new Date();

    currentUser: IUser;
    currentUserSubscription: Subscription;
    mediaWatcherSubscription: Subscription;

    allScholasticPeriods = [];

    displayedColumns = [];

    constructor(private storageService: StorageService, private appGlobals: AppGlobals, private authenticationService: AuthenticationService, private permitToStayService: PermitToStayService,
        public dialog: MatDialog, private mediaWatcherService: MediaWatcherService, private lookupsService: LookupsService, private orientationService: OrientationService, private router: Router,) {

    }

    ngOnInit() {
        this.appGlobals.pageTitleValue = "Orientation";

        this.mediaWatcherSubscription = this.mediaWatcherService.isTabletLarge.subscribe(isMobile => {
            this.isMobile = isMobile;

            if (this.isMobile)
                this.displayedColumns = ["scholasticPeriodDescription", "orientationSemesterClosingDate", "dateStarted", "dateCompleted", "orientationSemesterStudentStatusDescription", "preArrivalForm", "checkListItems"];
            else
                this.displayedColumns = ["statusId", "scholasticPeriodDescription", "orientationSemesterClosingDate", "dateStarted", "dateCompleted", "orientationSemesterStudentStatusDescription", "preArrivalForm", "checkListItems"];
        });

        this.currentUserSubscription = this.authenticationService.currentUser.subscribe(user => {
            this.currentUser = user;
        });

        //semesters
        this.lookupsService.getSemesters()
            .pipe(first())
            .subscribe((response: any) => {
                const semesters = response;

                this.getStudentOrientationSemesters();
            });
    }

    getStudentOrientationSemesters() {
        if (this.currentUser) {
            this.orientationService.getStudentOrientationSemesters(this.currentUser.studentId)
                .pipe(first())
                .subscribe(result => {
                    this.studentOrientationSemesters = _.orderBy(result, ["scholasticPeriodStartDate", "dateCreated"], ["desc", "desc"]);

                    _.forEach(this.studentOrientationSemesters, (f: any) => {
                        f.statusId = this.getOrientationSemesterStudentStatus(f);
                    });

                    //remove the incomplete orientation if completed
                    var missingDocuments = this.studentOrientationSemesters.filter(f => f.numRequiredDocuments > 0 && f.canBeModified);

                    if (!missingDocuments || missingDocuments.length === 0) {
                        if (this.currentUser.userNotifications.find(f => f.code === "orientationSemesterStudent")) {
                            _.remove(this.currentUser.userNotifications, f => f.code === "orientationSemesterStudent");

                            //check whether the student has any permits to stay
                            if (!this.currentUser.userNotifications.find(f => f.code === "orientationPermitToStayStudent")) {
                                this.permitToStayService.getStudentOrientationPermitsToStay(this.currentUser.studentId)
                                    .pipe(first())
                                    .subscribe((permitsToStay: any) => {
                                        let redirectUrl = null;

                                        if (permitsToStay.find(f => !f.dateCompleted)) {
                                            const tempStr = {
                                                code: "orientationPermitToStayStudent",
                                                message: "You have not yet <strong>Completed</strong> the <strong>Immigration</strong> Process.",
                                                viewed: false,
                                                url: "/permitToStay"
                                            };

                                            this.currentUser.userNotifications.push(tempStr);

                                            redirectUrl = "/permitToStay";
                                        }

                                        this.currentUser.canViewImmigration = permitsToStay?.length > 0;

                                        this.storageService.store(this.appGlobals.localStorageKeys.currentUser, this.currentUser);

                                        if (redirectUrl)
                                            this.router.navigate([redirectUrl], { skipLocationChange: true });
                                    });
                            }
                        }
                    }
                });
        }
    }

    ngOnDestroy() {
        // unsubscribe to ensure no memory leaks
        this.currentUserSubscription.unsubscribe();
        this.mediaWatcherSubscription.unsubscribe();
    }

    preArrivalForm(orientationSemesterStudentId: number, orientationSemesterId: number, age: number, isReturningStudent: boolean, requiresPreArrivalFormResubmission: boolean, scholasticPeriod: string, preArrivalFormDateSubmitted?: Date) {
        if (!isReturningStudent || (requiresPreArrivalFormResubmission && age >= 18)) {
            const dialogConfig = new MatDialogConfig();

            dialogConfig.disableClose = true;
            dialogConfig.autoFocus = true;
            dialogConfig.closeOnNavigation = true;
            dialogConfig.width = age < 18 ? "55%" : "90%";
            dialogConfig.height = "auto";
            dialogConfig.panelClass = "popup-dialog-container";

            dialogConfig.data = {
                currentUser: this.currentUser,
                orientationSemesterId: orientationSemesterId,
                orientationSemesterStudentId: orientationSemesterStudentId,
                scholasticPeriod: scholasticPeriod,
                age: age,
                allowEdit: !preArrivalFormDateSubmitted || (requiresPreArrivalFormResubmission && age >= 18)
            };

            const dialogRef = this.dialog.open(OrientationPreArrivalFormDialogComponent, dialogConfig);

            dialogRef.afterClosed().subscribe(() => {
                setTimeout(() => {
                    this.getStudentOrientationSemesters();
                }, 0);
            });
        }
    }

    checkListItems(orientationSemesterStudentId: number, studentAge: number, orientationSemesterClosingDate: any, scholasticPeriod: string, funnelId: number, funnel: string, isHousingStudent: boolean, isReturningStudent: boolean, provider: string, providerId?: number) {
        const dialogConfig = new MatDialogConfig();

        dialogConfig.disableClose = true;
        dialogConfig.autoFocus = true;
        dialogConfig.closeOnNavigation = true;
        dialogConfig.width = "90%";
        dialogConfig.height = "auto";
        dialogConfig.panelClass = "popup-dialog-container";

        dialogConfig.data = {
            orientationSemesterStudentId: orientationSemesterStudentId,
            scholasticPeriod: scholasticPeriod,
            funnelId: funnelId,
            funnel: funnel,
            isHousingStudent: isHousingStudent,
            isReturningStudent: isReturningStudent,
            provider: provider,
            providerId: providerId,
            studentAge: studentAge,
            isPastDeadline: this.today > new Date(orientationSemesterClosingDate)
        };

        const dialogRef = this.dialog.open(OrientationCheckListItemsDialogComponent, dialogConfig);

        dialogRef.afterClosed().subscribe(val => {
            if (val) {
                setTimeout(() => {
                    this.getStudentOrientationSemesters();
                }, 500);
            }
        });
    }

    getOrientationSemesterStudentStatus(semesterStudent: any) {
        let statusId = 1;

        //completed undergraduate
        if (semesterStudent.orientationSemesterStudentStatusId === 3 /*&& semesterStudent.schoolId === 1*/)
            statusId = 3;
        else {
            //if (semesterStudent.orientationSemesterStudentStatusId === 3 && semesterStudent.schoolId === 2)
            //    statusId = 4;
            //else {
            //pending
            if (semesterStudent.orientationSemesterStudentStatusId === 2)
                statusId = 2;
            else {
                //ignored
                if (semesterStudent.orientationSemesterStudentStatusId === 4)
                    statusId = 6;
                else {
                    //printed
                    if (semesterStudent.orientationSemesterStudentStatusId === 5)
                        statusId = 5;
                }
                //}
            }
        }

        return statusId;
    }
}
